import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';

import BlockedPage from '~/common/components/BlockedPage';
import Center from '~/common/components/Center';
import Spinner from '~/common/components/Spinner/index';
import { API_STATUS } from '~/common/constants';
import { useUser } from '~/common/hooks/useAuthentication';
import useBoltStatus from '~/common/hooks/useBoltStatus';
import useUrl from '~/common/hooks/useUrl';
import { useVARecoupmenTesttMutation } from '~/common/hooks/useVARecoupmentMutation';

import HomeContainer from './HomeContainer';
import { HomeActions, HomeThunks } from './redux';

const Home = () => {
  const queryClient = useQueryClient();
  // search for categoryId in URL
  const categoryId = new URLSearchParams(useLocation().search).get(
    'categoryId',
  );

  // local state
  const [showBlockedPage, setShowBlockedPage] = useState(null);
  const [isLoading, setIsLoading] = useState(null);
  const [_searchParams, setSearchParams] = useSearchParams();

  // redux
  const dispatch = useDispatch();
  const { obfuscatedLoanIdentifier } = useUrl();
  const { isUnderwriter } = useUser();
  const { obfuscatedLenderDatabaseId, obfuscatedLoanRecordId } =
    obfuscatedLoanIdentifier || {};
  const { navigationItems, onInit, triggerRules } = useSelector(({ home }) => ({
    ...home,
  }));
  const { isLoading: isBoltStatusLoading, isError: boltStatusError } =
    useBoltStatus();

  // loading check
  const isTriggerAllRulesLoading = triggerRules?.status === API_STATUS.GETTING;

  // error check
  const navigationItemsError = navigationItems.error;

  const { mutate: recoupmentMutate } = useVARecoupmenTesttMutation(
    obfuscatedLenderDatabaseId,
    obfuscatedLoanRecordId,
  );

  // call triggerAllRules, getNavigationDetails, getUnderwriterVerifications
  // on page load. triggerAllRules will be removed in the future according to
  // Soniya Pimple - Backend Architect. Check with her after AIME release.
  useEffect(() => {
    if (onInit && obfuscatedLenderDatabaseId && obfuscatedLoanRecordId) {
      (async () => {
        dispatch(HomeThunks.triggerAllRules());
        dispatch(HomeThunks.getNavigationDetails(false, isUnderwriter));
        recoupmentMutate();
        dispatch(HomeActions.setOnInit(false));
      })();
    }
  }, [
    onInit,
    obfuscatedLenderDatabaseId,
    obfuscatedLoanRecordId,
    dispatch,
    queryClient,
    recoupmentMutate,
    isUnderwriter,
  ]);

  useEffect(() => {
    const navigationType = performance.getEntriesByType(
      'navigation',
    )[0] as PerformanceNavigationTiming;
    const isReload = navigationType?.type === 'reload';

    const isClientSideNavigation =
      sessionStorage.getItem('clientNavigation') === 'true';

    if (isReload && !isClientSideNavigation) {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        if (params.has('insuranceDocumentType') || params.has('ruleId')) {
          params.delete('insuranceDocumentType');
          params.delete('ruleId');
          return params;
        }
        return prev;
      });
    }

    sessionStorage.removeItem('clientNavigation');
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, []);

  // select category based on URL
  useEffect(() => {
    if (!isLoading && categoryId && navigationItems.allAlerts.length) {
      dispatch(HomeActions.setSelectedCategory(Number.parseInt(categoryId)));
      dispatch(
        HomeActions.setSelectedAlert(
          navigationItems.allAlerts.find(
            (x) => x.ruleCategoryId === +categoryId,
          ),
        ),
      );
      globalThis.history.pushState(
        null,
        '',
        `/${obfuscatedLenderDatabaseId}/${obfuscatedLoanRecordId}`,
      );
    }
  }, [
    dispatch,
    categoryId,
    isLoading,
    obfuscatedLenderDatabaseId,
    obfuscatedLoanRecordId,
    navigationItems,
  ]);

  // isLoading check
  useEffect(() => {
    if (onInit || isBoltStatusLoading || isTriggerAllRulesLoading) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [onInit, isBoltStatusLoading, isTriggerAllRulesLoading]);

  // blocked page check
  useEffect(() => {
    if (!obfuscatedLoanRecordId || !obfuscatedLenderDatabaseId) {
      setShowBlockedPage(true);
    } else if (boltStatusError || navigationItemsError) {
      setShowBlockedPage(true);
    } else {
      setShowBlockedPage(false);
    }
  }, [
    boltStatusError,
    navigationItemsError,
    obfuscatedLenderDatabaseId,
    obfuscatedLoanRecordId,
  ]);
  if (showBlockedPage) {
    return <BlockedPage />;
  }

  if (isLoading) {
    return (
      <Center width="100%" height="100%">
        <Spinner />
      </Center>
    );
  }

  return <HomeContainer />;
};

export default Home;
